import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Users, Zap, Target, CheckCircle, ArrowRight, Award, Briefcase, Clock, DollarSign, Globe, Headphones, LineChart, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';

const WhiteLabelAgencyPage: React.FC = () => {
  const features = [
    {
      icon: <Shield className="w-8 h-8 text-jordy-blue-500" />,
      title: "White Label Services",
      description: "Full-service digital solutions under your brand name, maintaining your identity while we handle the heavy lifting."
    },
    {
      icon: <Users className="w-8 h-8 text-jordy-blue-500" />,
      title: "Dedicated Team",
      description: "Expert professionals working exclusively on your projects, ensuring consistent quality and timely delivery."
    },
    {
      icon: <Zap className="w-8 h-8 text-jordy-blue-500" />,
      title: "Quick Deployment",
      description: "Fast turnaround times with our streamlined processes and experienced development teams."
    },
    {
      icon: <Target className="w-8 h-8 text-jordy-blue-500" />,
      title: "Scalable Solutions",
      description: "Flexible resources that grow with your business, from small projects to enterprise solutions."
    }
  ];

  const benefits = [
    {
      title: "No overhead costs",
      description: "Save on hiring, training, and infrastructure costs"
    },
    {
      title: "Experienced development team",
      description: "Access to skilled professionals across technologies"
    },
    {
      title: "Competitive pricing",
      description: "Cost-effective solutions with transparent pricing"
    },
    {
      title: "Quick turnaround time",
      description: "Faster delivery without compromising quality"
    },
    {
      title: "White label reporting",
      description: "Branded reports and dashboards for your clients"
    },
    {
      title: "Dedicated project manager",
      description: "Single point of contact for smooth communication"
    },
    {
      title: "24/7 support",
      description: "Round-the-clock assistance for critical issues"
    },
    {
      title: "Custom solutions",
      description: "Tailored development to match your needs"
    }
  ];

  const services = [
    {
      icon: <Globe className="w-12 h-12 text-jordy-blue-500" />,
      title: "Web Development",
      items: ["Custom Websites", "Web Applications", "E-commerce Solutions", "CMS Development"]
    },
    {
      icon: <LineChart className="w-12 h-12 text-jordy-blue-500" />,
      title: "Digital Marketing",
      items: ["SEO Services", "PPC Management", "Social Media", "Content Marketing"]
    },
    {
      icon: <Briefcase className="w-12 h-12 text-jordy-blue-500" />,
      title: "Business Solutions",
      items: ["CRM Systems", "ERP Solutions", "Business Intelligence", "Process Automation"]
    },
    {
      icon: <Headphones className="w-12 h-12 text-jordy-blue-500" />,
      title: "Support Services",
      items: ["24/7 Help Desk", "System Maintenance", "Security Updates", "Performance Monitoring"]
    }
  ];

  const process = [
    {
      icon: <MessageSquare className="w-8 h-8 text-jordy-blue-500" />,
      title: "Initial Consultation",
      description: "We discuss your needs and how we can help scale your agency"
    },
    {
      icon: <Award className="w-8 h-8 text-jordy-blue-500" />,
      title: "Partnership Agreement",
      description: "Clear terms and pricing structure for long-term success"
    },
    {
      icon: <Clock className="w-8 h-8 text-jordy-blue-500" />,
      title: "Onboarding Process",
      description: "Quick setup and training for seamless integration"
    },
    {
      icon: <DollarSign className="w-8 h-8 text-jordy-blue-500" />,
      title: "Start Earning",
      description: "Begin offering services and growing your revenue"
    }
  ];

  return (
    <>
      <Helmet>
        <title>White Label Agency Services - VB Entreprise</title>
        <meta name="description" content="Partner with VB Entreprise as your white label digital agency. We provide comprehensive digital solutions under your brand." />
      </Helmet>

      <div className="bg-gradient-to-b from-white to-jordy-blue-50">
        {/* Hero Section */}
        <AnimatedSection>
          <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="text-center">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  Your Success, <span className="text-jordy-blue-600">Our Expertise</span>
                </h1>
                <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                  Launch your own digital agency without the overhead. We provide comprehensive white label services that help you scale your business while maintaining your brand identity.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center px-8 py-4 bg-jordy-blue-600 text-white font-semibold rounded-lg hover:bg-jordy-blue-700 transition-colors duration-200"
                >
                  Start Your Agency Today
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Features Grid */}
        <AnimatedSection>
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Complete White Label Solutions
                </h2>
                <p className="text-xl text-gray-600">
                  Everything you need to run a successful digital agency
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-200 border border-gray-100"
                  >
                    <div className="mb-4">{feature.icon}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Services Section */}
        <AnimatedSection>
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-jordy-blue-50">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Our White Label Services
                </h2>
                <p className="text-xl text-gray-600">
                  Comprehensive solutions to help you serve any client
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {services.map((service, index) => (
                  <div key={index} className="bg-white p-8 rounded-xl shadow-lg">
                    <div className="flex justify-center mb-6">{service.icon}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                      {service.title}
                    </h3>
                    <ul className="space-y-3">
                      {service.items.map((item, idx) => (
                        <li key={idx} className="flex items-center text-gray-600">
                          <CheckCircle className="w-5 h-5 text-jordy-blue-500 mr-2" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Benefits Section */}
        <AnimatedSection>
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Why Choose Our White Label Services?
                </h2>
                <p className="text-xl text-gray-600">
                  Partner with us and get access to premium digital solutions
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="bg-white p-6 rounded-lg shadow-md border border-gray-100 hover:border-jordy-blue-200 transition-colors duration-200"
                  >
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="w-6 h-6 text-jordy-blue-500 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-gray-900">{benefit.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{benefit.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* Process Section */}
        <AnimatedSection>
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-jordy-blue-50">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Simple Partnership Process
                </h2>
                <p className="text-xl text-gray-600">
                  Get started with our white label services in four easy steps
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {process.map((step, index) => (
                  <div key={index} className="relative">
                    <div className="bg-white p-8 rounded-xl shadow-lg text-center">
                      <div className="flex justify-center mb-6">{step.icon}</div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {step.title}
                      </h3>
                      <p className="text-gray-600">{step.description}</p>
                    </div>
                    {index < process.length - 1 && (
                      <div className="hidden lg:block absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2">
                        <ArrowRight className="w-8 h-8 text-jordy-blue-300" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* CTA Section */}
        <AnimatedSection>
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-jordy-blue-600">
            <div className="max-w-7xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-white mb-6">
                Ready to Scale Your Agency?
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Join hundreds of successful agencies who trust us with their client projects. Start growing your business today!
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center px-8 py-4 bg-white text-jordy-blue-600 font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  Schedule a Call
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
                <Link
                  to="/partner"
                  className="inline-flex items-center px-8 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-white hover:bg-white/10 transition-colors duration-200"
                >
                  View Partnership Details
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            </div>
          </section>
        </AnimatedSection>
      </div>
    </>
  );
};

export default WhiteLabelAgencyPage; 